import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  TreeDeciduous, Home, Droplets, Zap, Plus, Trash2, Save, Printer,
  Clock, Calendar, ShoppingCart, CheckCircle, Image, Loader2, GripVertical,
  ChevronRight, Download, Leaf, Sun, Fence, CircleDot, Square
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Existing item types with icons and colors
const ITEM_TYPES = {
  tree: {
    categories: [
      { id: "large_tree", name: "Large Tree (>30ft)", icon: "🌳", color: "#2C4A3B", defaultSize: { w: 8, h: 8 } },
      { id: "medium_tree", name: "Medium Tree (15-30ft)", icon: "🌲", color: "#3D6B4F", defaultSize: { w: 5, h: 5 } },
      { id: "small_tree", name: "Small Tree (<15ft)", icon: "🌱", color: "#4CAF50", defaultSize: { w: 3, h: 3 } },
    ]
  },
  structure: {
    categories: [
      { id: "shed", name: "Shed/Outbuilding", icon: "🏠", color: "#8B4513", defaultSize: { w: 3, h: 4 } },
      { id: "fence", name: "Fence Line", icon: "🚧", color: "#A0522D", defaultSize: { w: 0.3, h: 10 } },
      { id: "patio", name: "Patio/Deck", icon: "⬛", color: "#696969", defaultSize: { w: 4, h: 4 } },
      { id: "raised_bed", name: "Raised Bed", icon: "📦", color: "#CD853F", defaultSize: { w: 1.2, h: 2.4 } },
      { id: "greenhouse", name: "Greenhouse", icon: "🏡", color: "#87CEEB", defaultSize: { w: 3, h: 4 } },
    ]
  },
  utility: {
    categories: [
      { id: "water_spigot", name: "Water Spigot", icon: "🚿", color: "#4169E1", defaultSize: { w: 0.5, h: 0.5 } },
      { id: "power_outlet", name: "Power Outlet", icon: "⚡", color: "#FFD700", defaultSize: { w: 0.5, h: 0.5 } },
      { id: "septic", name: "Septic/Drain Field", icon: "⚠️", color: "#FF6347", defaultSize: { w: 6, h: 4 } },
      { id: "well", name: "Well", icon: "💧", color: "#00CED1", defaultSize: { w: 1, h: 1 } },
      { id: "power_line", name: "Power Line (overhead)", icon: "〰️", color: "#333", defaultSize: { w: 0.2, h: 15 } },
    ]
  },
  custom: {
    categories: [
      { id: "custom", name: "Custom Item", icon: "📍", color: "#9370DB", defaultSize: { w: 1, h: 1 } },
    ]
  }
};

const GardenPlannerPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("existing");
  const [existingItems, setExistingItems] = useState([]);
  const [actionPlan, setActionPlan] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [futureViz, setFutureViz] = useState(null);
  const [selectedYear, setSelectedYear] = useState(5);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [futureImage, setFutureImage] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // For adding new items
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [newItemName, setNewItemName] = useState("");
  
  useEffect(() => {
    if (planId) {
      loadPlan();
    }
  }, [planId]);
  
  const loadPlan = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans/${planId}`, { withCredentials: true });
      setPlan(response.data);
      setExistingItems(response.data.existing_items || []);
      setActionPlan(response.data.action_plan || []);
      setShoppingList(response.data.shopping_list || []);
    } catch (error) {
      toast.error("Failed to load garden plan");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  // Canvas drawing for existing items
  useEffect(() => {
    if (!canvasRef.current || !plan) return;
    drawCanvas();
  }, [plan, existingItems, selectedYear, futureViz]);
  
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear
    ctx.fillStyle = "#F5F5F0";
    ctx.fillRect(0, 0, width, height);
    
    // Grid
    ctx.strokeStyle = "rgba(44, 74, 59, 0.1)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= width; x += 20) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y <= height; y += 20) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
    
    // Scale calculation
    const scaleX = (width - 60) / plan.width;
    const scaleY = (height - 60) / plan.length;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (width - plan.width * scale) / 2;
    const offsetY = (height - plan.length * scale) / 2;
    
    // Garden boundary
    ctx.strokeStyle = "#2C4A3B";
    ctx.lineWidth = 3;
    ctx.strokeRect(offsetX, offsetY, plan.width * scale, plan.length * scale);
    
    // Draw existing items
    existingItems.forEach(item => {
      const x = offsetX + item.x * scale;
      const y = offsetY + item.y * scale;
      const w = item.width * scale;
      const h = item.height * scale;
      
      // Find category info
      let catInfo = null;
      for (const type of Object.values(ITEM_TYPES)) {
        catInfo = type.categories.find(c => c.id === item.category);
        if (catInfo) break;
      }
      
      ctx.fillStyle = item.color || catInfo?.color || "#888";
      ctx.globalAlpha = 0.6;
      
      if (item.item_type === "tree" && item.canopy_radius) {
        // Draw tree as circle
        const radius = item.canopy_radius * scale;
        ctx.beginPath();
        ctx.arc(x + w/2, y + h/2, radius, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, w, h);
      }
      
      ctx.globalAlpha = 1;
      ctx.strokeStyle = item.color || catInfo?.color || "#888";
      ctx.lineWidth = 2;
      
      if (item.item_type === "tree" && item.canopy_radius) {
        const radius = item.canopy_radius * scale;
        ctx.beginPath();
        ctx.arc(x + w/2, y + h/2, radius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(x, y, w, h);
      }
      
      // Label
      ctx.fillStyle = "#2C2C2C";
      ctx.font = "10px Manrope";
      ctx.textAlign = "center";
      ctx.fillText(item.name.substring(0, 12), x + w/2, y + h/2 + 4);
    });
    
    // Draw future plant projections if viewing future
    if (futureViz && activeTab === "future") {
      const yearKey = `${selectedYear}_years`;
      const projection = futureViz[yearKey];
      if (projection?.plants) {
        // Sort plants by layer for proper z-ordering (ground first, canopy last)
        const layerOrder = ["ground_cover", "root", "herbaceous", "shrub", "vine", "understory", "canopy"];
        const sortedPlants = [...projection.plants].sort((a, b) => 
          layerOrder.indexOf(a.layer) - layerOrder.indexOf(b.layer)
        );
        
        // Distribute plants across the garden space
        const gardenWidth = plan.width * scale;
        const gardenHeight = plan.length * scale;
        const cols = Math.ceil(Math.sqrt(sortedPlants.length));
        const rows = Math.ceil(sortedPlants.length / cols);
        const cellWidth = gardenWidth / (cols + 1);
        const cellHeight = gardenHeight / (rows + 1);
        
        sortedPlants.forEach((plant, idx) => {
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          
          // Position within garden
          const x = offsetX + cellWidth * (col + 1);
          const y = offsetY + cellHeight * (row + 0.5);
          
          // Size based on projected dimensions
          const plantRadius = Math.max(15, plant.projected_width * scale * 0.4);
          const plantHeight = plant.projected_height * scale * 0.3;
          
          const layerColors = {
            canopy: "#1B5E20",
            understory: "#2E7D32",
            shrub: "#388E3C",
            herbaceous: "#4CAF50",
            ground_cover: "#81C784",
            vine: "#66BB6A",
            root: "#A1887F"
          };
          
          const color = layerColors[plant.layer] || "#4CAF50";
          
          // Draw plant shadow
          ctx.fillStyle = "rgba(0,0,0,0.1)";
          ctx.beginPath();
          ctx.ellipse(x, y + plantRadius * 0.3, plantRadius * 0.8, plantRadius * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();
          
          // Draw plant canopy/foliage
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.7;
          
          if (plant.layer === "canopy" || plant.layer === "understory") {
            // Tree-like shape
            ctx.beginPath();
            ctx.arc(x, y - plantHeight * 0.3, plantRadius, 0, Math.PI * 2);
            ctx.fill();
            // Trunk
            ctx.fillStyle = "#5D4037";
            ctx.fillRect(x - 3, y - plantHeight * 0.3, 6, plantHeight * 0.5);
          } else if (plant.layer === "shrub") {
            // Bush shape - multiple overlapping circles
            ctx.beginPath();
            ctx.arc(x, y, plantRadius * 0.8, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(x - plantRadius * 0.4, y + 5, plantRadius * 0.6, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(x + plantRadius * 0.4, y + 5, plantRadius * 0.6, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Herbaceous/ground cover - lower, spread out
            ctx.beginPath();
            ctx.ellipse(x, y, plantRadius, plantRadius * 0.5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          
          ctx.globalAlpha = 1;
          
          // Plant label
          ctx.fillStyle = "#1a1a1a";
          ctx.font = "bold 10px Manrope";
          ctx.textAlign = "center";
          ctx.fillText(plant.name.substring(0, 10), x, y + plantRadius + 12);
          
          // Size indicator
          ctx.font = "9px Manrope";
          ctx.fillStyle = "#666";
          ctx.fillText(`${plant.projected_height}m`, x, y + plantRadius + 22);
        });
        
        // Draw year indicator
        ctx.fillStyle = "#2C4A3B";
        ctx.font = "bold 16px Manrope";
        ctx.textAlign = "left";
        ctx.fillText(`Year ${selectedYear}`, offsetX + 10, offsetY + 25);
        
        // Draw canopy coverage bar
        const barWidth = 100;
        const barHeight = 12;
        const barX = offsetX + 10;
        const barY = offsetY + 35;
        
        ctx.fillStyle = "#e0e0e0";
        ctx.fillRect(barX, barY, barWidth, barHeight);
        ctx.fillStyle = "#4CAF50";
        ctx.fillRect(barX, barY, barWidth * (projection.canopy_coverage / 100), barHeight);
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.strokeRect(barX, barY, barWidth, barHeight);
        
        ctx.fillStyle = "#333";
        ctx.font = "10px Manrope";
        ctx.fillText(`Canopy: ${projection.canopy_coverage}%`, barX + barWidth + 8, barY + 10);
      }
    }
    
    // Compass
    const compassX = width - 40;
    const compassY = 40;
    ctx.beginPath();
    ctx.arc(compassX, compassY, 20, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    ctx.strokeStyle = "#2C4A3B";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(compassX, compassY - 15);
    ctx.lineTo(compassX - 5, compassY);
    ctx.lineTo(compassX + 5, compassY);
    ctx.closePath();
    ctx.fillStyle = "#D66A48";
    ctx.fill();
    ctx.fillStyle = "#2C4A3B";
    ctx.font = "bold 10px Space Mono";
    ctx.textAlign = "center";
    ctx.fillText("N", compassX, compassY - 5);
    
    // Scale indicator
    ctx.fillStyle = "#2C4A3B";
    ctx.font = "11px Space Mono";
    ctx.textAlign = "left";
    ctx.fillText(`${plan.width}m × ${plan.length}m`, 10, height - 10);
  };
  
  const handleCanvasClick = (e) => {
    if (!selectedItemType || activeTab !== "existing") return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleRatio = canvas.width / rect.width;
    const clickX = (e.clientX - rect.left) * scaleRatio;
    const clickY = (e.clientY - rect.top) * scaleRatio;
    
    // Calculate position in meters
    const scale = Math.min((canvas.width - 60) / plan.width, (canvas.height - 60) / plan.length);
    const offsetX = (canvas.width - plan.width * scale) / 2;
    const offsetY = (canvas.height - plan.length * scale) / 2;
    
    const meterX = (clickX - offsetX) / scale;
    const meterY = (clickY - offsetY) / scale;
    
    if (meterX < 0 || meterX > plan.width || meterY < 0 || meterY > plan.length) {
      return; // Click outside garden bounds
    }
    
    // Find category info
    let catInfo = null;
    let itemType = null;
    for (const [type, data] of Object.entries(ITEM_TYPES)) {
      catInfo = data.categories.find(c => c.id === selectedItemType);
      if (catInfo) {
        itemType = type;
        break;
      }
    }
    
    const newItem = {
      id: `item_${Date.now()}`,
      item_type: itemType,
      category: selectedItemType,
      name: newItemName || catInfo?.name || "Item",
      x: Math.max(0, meterX - (catInfo?.defaultSize.w || 1) / 2),
      y: Math.max(0, meterY - (catInfo?.defaultSize.h || 1) / 2),
      width: catInfo?.defaultSize.w || 1,
      height: catInfo?.defaultSize.h || 1,
      color: catInfo?.color,
      canopy_radius: itemType === "tree" ? (catInfo?.defaultSize.w || 2) / 2 : null
    };
    
    setExistingItems([...existingItems, newItem]);
    setSelectedItemType(null);
    setNewItemName("");
    toast.success(`Added ${newItem.name}`);
  };
  
  const removeItem = (itemId) => {
    setExistingItems(existingItems.filter(i => i.id !== itemId));
  };
  
  const saveExistingItems = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/garden-plans/${planId}/existing-items`, existingItems, { withCredentials: true });
      toast.success("Existing items saved!");
    } catch (error) {
      toast.error("Failed to save items");
    } finally {
      setSaving(false);
    }
  };
  
  // Action Plan functions
  const generateActionPlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/garden-plans/${planId}/generate-action-plan`, {}, { withCredentials: true });
      setActionPlan(response.data.actions);
      toast.success("Action plan generated!");
    } catch (error) {
      toast.error("Failed to generate action plan");
    } finally {
      setLoading(false);
    }
  };
  
  const toggleActionComplete = (actionId) => {
    setActionPlan(actionPlan.map(a => 
      a.id === actionId ? { ...a, completed: !a.completed } : a
    ));
  };
  
  const addActionItem = () => {
    const newAction = {
      id: `action_${Date.now()}`,
      task: "New Task",
      description: "",
      season: "any",
      priority: "medium",
      completed: false,
      order: actionPlan.length
    };
    setActionPlan([...actionPlan, newAction]);
  };
  
  const updateActionItem = (id, field, value) => {
    setActionPlan(actionPlan.map(a => 
      a.id === id ? { ...a, [field]: value } : a
    ));
  };
  
  const removeActionItem = (id) => {
    setActionPlan(actionPlan.filter(a => a.id !== id));
  };
  
  const saveActionPlan = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/garden-plans/${planId}/action-plan`, actionPlan, { withCredentials: true });
      toast.success("Action plan saved!");
    } catch (error) {
      toast.error("Failed to save action plan");
    } finally {
      setSaving(false);
    }
  };
  
  // Shopping List functions
  const generateShoppingList = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/garden-plans/${planId}/generate-shopping-list`, {}, { withCredentials: true });
      setShoppingList(response.data.items);
      toast.success("Shopping list generated!");
    } catch (error) {
      toast.error("Failed to generate shopping list");
    } finally {
      setLoading(false);
    }
  };
  
  const toggleShoppingPurchased = (itemId) => {
    setShoppingList(shoppingList.map(i => 
      i.id === itemId ? { ...i, purchased: !i.purchased } : i
    ));
  };
  
  const addShoppingItem = () => {
    const newItem = {
      id: `shop_${Date.now()}`,
      name: "New Item",
      category: "other",
      quantity: "1",
      estimated_cost: null,
      purchased: false
    };
    setShoppingList([...shoppingList, newItem]);
  };
  
  const updateShoppingItem = (id, field, value) => {
    setShoppingList(shoppingList.map(i => 
      i.id === id ? { ...i, [field]: value } : i
    ));
  };
  
  const removeShoppingItem = (id) => {
    setShoppingList(shoppingList.filter(i => i.id !== id));
  };
  
  const saveShoppingList = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/garden-plans/${planId}/shopping-list`, shoppingList, { withCredentials: true });
      toast.success("Shopping list saved!");
    } catch (error) {
      toast.error("Failed to save shopping list");
    } finally {
      setSaving(false);
    }
  };
  
  // Future Visualization
  const loadFutureVisualization = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/garden-plans/${planId}/future-visualization`, {}, { withCredentials: true });
      setFutureViz(response.data);
    } catch (error) {
      toast.error("Failed to load future visualization");
    } finally {
      setLoading(false);
    }
  };
  
  const generateFutureImage = async () => {
    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/garden-plans/${planId}/generate-future-image`, 
        { years: selectedYear }, 
        { withCredentials: true }
      );
      setFutureImage(response.data.image_url);
      toast.success("Image generated!");
    } catch (error) {
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(false);
    }
  };
  
  // Print functions
  const printActionPlan = () => {
    const printContent = `
      <html>
      <head>
        <title>${plan?.name || "Garden"} - Action Plan</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #2C4A3B; border-bottom: 2px solid #2C4A3B; padding-bottom: 10px; }
          .task { padding: 10px; margin: 10px 0; border-left: 4px solid #4CAF50; background: #f5f5f5; }
          .task.completed { opacity: 0.6; text-decoration: line-through; }
          .priority-high { border-left-color: #f44336; }
          .priority-medium { border-left-color: #ff9800; }
          .priority-low { border-left-color: #4CAF50; }
          .checkbox { width: 16px; height: 16px; border: 1px solid #333; display: inline-block; margin-right: 10px; }
          .season { font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>🌱 ${plan?.name || "Garden"} - Action Plan</h1>
        <p>Generated by RootSpace.pro</p>
        ${actionPlan.map(a => `
          <div class="task priority-${a.priority} ${a.completed ? 'completed' : ''}">
            <span class="checkbox">${a.completed ? '✓' : ''}</span>
            <strong>${a.task}</strong>
            <p>${a.description || ''}</p>
            <span class="season">Season: ${a.season} | Priority: ${a.priority} ${a.estimated_time ? `| Time: ${a.estimated_time}` : ''}</span>
          </div>
        `).join('')}
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };
  
  const printShoppingList = () => {
    const total = shoppingList.reduce((sum, i) => sum + (i.estimated_cost || 0), 0);
    const printContent = `
      <html>
      <head>
        <title>${plan?.name || "Garden"} - Shopping List</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #2C4A3B; border-bottom: 2px solid #2C4A3B; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #2C4A3B; color: white; }
          .purchased { opacity: 0.6; text-decoration: line-through; }
          .checkbox { width: 16px; height: 16px; border: 1px solid #333; display: inline-block; }
          .total { font-weight: bold; font-size: 18px; margin-top: 20px; }
        </style>
      </head>
      <body>
        <h1>🛒 ${plan?.name || "Garden"} - Shopping List</h1>
        <p>Generated by RootSpace.pro</p>
        <table>
          <tr>
            <th>✓</th>
            <th>Item</th>
            <th>Category</th>
            <th>Qty</th>
            <th>Est. Cost</th>
            <th>Notes</th>
          </tr>
          ${shoppingList.map(i => `
            <tr class="${i.purchased ? 'purchased' : ''}">
              <td><span class="checkbox">${i.purchased ? '✓' : ''}</span></td>
              <td>${i.name}</td>
              <td>${i.category}</td>
              <td>${i.quantity}</td>
              <td>${i.estimated_cost ? '$' + i.estimated_cost.toFixed(2) : '-'}</td>
              <td>${i.notes || ''}</td>
            </tr>
          `).join('')}
        </table>
        <p class="total">Estimated Total: $${total.toFixed(2)}</p>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };
  
  if (loading && !plan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{plan?.name}</h1>
              <p className="text-sm text-muted-foreground">{plan?.location}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/plan/${planId}`)}>
              View Full Plan
            </Button>
          </div>
        </div>
      </header>
      
      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: "existing", label: "Existing Items", icon: TreeDeciduous },
            { id: "future", label: "Future View", icon: Sun },
            { id: "actions", label: "Action Plan", icon: CheckCircle },
            { id: "shopping", label: "Shopping List", icon: ShoppingCart }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "future" && !futureViz) loadFutureVisualization();
              }}
              className="flex items-center gap-2 whitespace-nowrap"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 pb-12">
        <AnimatePresence mode="wait">
          {/* Existing Items Tab */}
          {activeTab === "existing" && (
            <motion.div
              key="existing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="grid lg:grid-cols-3 gap-6"
            >
              {/* Item Palette */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Add Existing Items
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Select an item type, then click on the garden to place it.
                  </p>
                  
                  {Object.entries(ITEM_TYPES).map(([type, data]) => (
                    <div key={type}>
                      <h4 className="font-medium capitalize mb-2 flex items-center gap-2">
                        {type === "tree" && <TreeDeciduous className="w-4 h-4" />}
                        {type === "structure" && <Home className="w-4 h-4" />}
                        {type === "utility" && <Zap className="w-4 h-4" />}
                        {type === "custom" && <CircleDot className="w-4 h-4" />}
                        {type}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {data.categories.map(cat => (
                          <Button
                            key={cat.id}
                            variant={selectedItemType === cat.id ? "default" : "outline"}
                            size="sm"
                            className="justify-start text-xs h-auto py-2"
                            onClick={() => setSelectedItemType(cat.id)}
                          >
                            <span className="mr-1">{cat.icon}</span>
                            {cat.name.split(" ")[0]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {selectedItemType && (
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Label className="text-sm">Custom Name (optional)</Label>
                      <Input
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        placeholder="e.g., Old Oak Tree"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Click on the garden to place the item
                      </p>
                    </div>
                  )}
                  
                  <Button onClick={saveExistingItems} className="w-full" disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Items"}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Canvas */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Your Garden ({plan?.width}m × {plan?.length}m)</CardTitle>
                </CardHeader>
                <CardContent>
                  <canvas
                    ref={canvasRef}
                    width={700}
                    height={500}
                    onClick={handleCanvasClick}
                    className="w-full border rounded-lg cursor-crosshair"
                    style={{ cursor: selectedItemType ? 'crosshair' : 'default' }}
                    data-testid="garden-canvas-editor"
                  />
                  
                  {/* Items List */}
                  {existingItems.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="font-medium text-sm">Placed Items ({existingItems.length})</h4>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {existingItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                            <span>{item.name} ({item.x.toFixed(1)}m, {item.y.toFixed(1)}m)</span>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
          
          {/* Future Visualization Tab */}
          {activeTab === "future" && (
            <motion.div
              key="future"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Year Selector */}
              <div className="flex items-center justify-center gap-4">
                {[3, 5, 10].map(year => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? "default" : "outline"}
                    size="lg"
                    onClick={() => setSelectedYear(year)}
                    className="min-w-[100px]"
                    data-testid={`year-${year}`}
                  >
                    {year} Years
                  </Button>
                ))}
              </div>
              
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Visual Diagram */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Leaf className="w-5 h-5" />
                      Garden at {selectedYear} Years
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <canvas
                      ref={canvasRef}
                      width={700}
                      height={500}
                      className="w-full border rounded-lg"
                    />
                    
                    {/* Side View Growth Comparison */}
                    {futureViz && (
                      <div className="mt-4 p-4 bg-gradient-to-b from-sky-100 to-amber-50 rounded-lg">
                        <h4 className="font-medium text-sm mb-3">Side View - Plant Heights at Year {selectedYear}</h4>
                        <div className="relative h-48 flex items-end justify-around gap-2 border-b-2 border-amber-700">
                          {/* Height scale */}
                          <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col justify-between text-xs text-muted-foreground">
                            <span>15m</span>
                            <span>10m</span>
                            <span>5m</span>
                            <span>0m</span>
                          </div>
                          
                          {/* Plants */}
                          <div className="flex-1 flex items-end justify-around gap-1 ml-10">
                            {futureViz[`${selectedYear}_years`]?.plants?.map((plant, idx) => {
                              const maxHeight = 15; // max display height in meters
                              const heightPercent = Math.min(100, (plant.projected_height / maxHeight) * 100);
                              const layerColors = {
                                canopy: "bg-green-800",
                                understory: "bg-green-700",
                                shrub: "bg-green-600",
                                herbaceous: "bg-green-500",
                                ground_cover: "bg-green-400",
                                vine: "bg-green-500",
                                root: "bg-amber-600"
                              };
                              const bgColor = layerColors[plant.layer] || "bg-green-500";
                              
                              return (
                                <div key={idx} className="flex flex-col items-center" style={{ minWidth: '60px' }}>
                                  <div 
                                    className={`${bgColor} rounded-t-full w-8 transition-all duration-500`}
                                    style={{ 
                                      height: `${Math.max(8, heightPercent)}%`,
                                      minHeight: '20px'
                                    }}
                                  />
                                  {(plant.layer === "canopy" || plant.layer === "understory") && (
                                    <div className="w-1 h-4 bg-amber-800" />
                                  )}
                                  <span className="text-xs mt-1 text-center font-medium truncate w-full">{plant.name.split(' ')[0]}</span>
                                  <span className="text-xs text-muted-foreground">{plant.projected_height}m</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {/* Ground line label */}
                        <div className="flex justify-center mt-1">
                          <span className="text-xs text-amber-700">Ground Level</span>
                        </div>
                      </div>
                    )}
                    
                    {futureViz && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <h4 className="font-medium">{futureViz[`${selectedYear}_years`]?.description}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Canopy Coverage: {futureViz[`${selectedYear}_years`]?.canopy_coverage}%
                        </p>
                        <ul className="mt-2 text-sm space-y-1">
                          {futureViz[`${selectedYear}_years`]?.notes?.map((note, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 text-primary mt-0.5" />
                              {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Plant Projections & AI Image */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Image className="w-5 h-5" />
                        AI Garden Render
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {futureImage ? (
                        <div className="space-y-4">
                          <img 
                            src={futureImage} 
                            alt={`Garden at ${selectedYear} years`} 
                            className="w-full rounded-lg"
                          />
                          <Button 
                            variant="outline" 
                            onClick={() => setFutureImage(null)}
                            className="w-full"
                          >
                            Generate New Image
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground mb-4">
                            Generate an AI visualization of your garden at {selectedYear} years
                          </p>
                          <Button onClick={generateFutureImage} disabled={generatingImage}>
                            {generatingImage ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Image className="w-4 h-4 mr-2" />
                                Generate Image
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Plant Growth Table */}
                  {futureViz && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Plant Growth Projections</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-card">
                              <tr className="border-b">
                                <th className="text-left py-2">Plant</th>
                                <th className="text-right py-2">Height</th>
                                <th className="text-right py-2">Width</th>
                                <th className="text-right py-2">Maturity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {futureViz[`${selectedYear}_years`]?.plants?.map((plant, i) => (
                                <tr key={i} className="border-b border-muted">
                                  <td className="py-2">{plant.name}</td>
                                  <td className="text-right">{plant.projected_height}m</td>
                                  <td className="text-right">{plant.projected_width}m</td>
                                  <td className="text-right">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                      plant.maturity_percent >= 80 ? 'bg-green-100 text-green-700' :
                                      plant.maturity_percent >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-orange-100 text-orange-700'
                                    }`}>
                                      {plant.maturity_percent}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Timeline Comparison - All Years */}
                  {futureViz && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Growth Timeline Comparison</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {[3, 5, 10].map(year => {
                            const yearData = futureViz[`${year}_years`];
                            if (!yearData) return null;
                            return (
                              <div 
                                key={year} 
                                className={`p-3 rounded-lg border-2 transition-all ${
                                  selectedYear === year 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-muted hover:border-primary/50 cursor-pointer'
                                }`}
                                onClick={() => setSelectedYear(year)}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-bold text-lg">Year {year}</span>
                                  <span className="text-sm text-muted-foreground">{yearData.description}</span>
                                </div>
                                <div className="flex gap-4 text-sm">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-green-500" />
                                    <span>Canopy: {yearData.canopy_coverage}%</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <TreeDeciduous className="w-4 h-4 text-green-600" />
                                    <span>{yearData.plants?.length || 0} plants</span>
                                  </div>
                                </div>
                                {/* Mini growth bars */}
                                <div className="flex gap-1 mt-2">
                                  {yearData.plants?.slice(0, 6).map((p, i) => (
                                    <div 
                                      key={i}
                                      className="flex-1 bg-green-200 rounded-t overflow-hidden"
                                      title={`${p.name}: ${p.projected_height}m`}
                                    >
                                      <div 
                                        className="bg-green-600 w-full transition-all"
                                        style={{ height: `${Math.min(100, p.maturity_percent)}%`, minHeight: '4px' }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Action Plan Tab */}
          {activeTab === "actions" && (
            <motion.div
              key="actions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Step-by-Step Action Plan
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={generateActionPlan}>
                      Auto-Generate
                    </Button>
                    <Button variant="outline" size="sm" onClick={addActionItem}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                    <Button variant="outline" size="sm" onClick={printActionPlan}>
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                    <Button size="sm" onClick={saveActionPlan} disabled={saving}>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {actionPlan.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No action items yet</p>
                      <Button onClick={generateActionPlan} className="mt-4">
                        Generate Action Plan
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {actionPlan.sort((a, b) => a.order - b.order).map((action, idx) => (
                        <div
                          key={action.id}
                          className={`flex items-start gap-3 p-4 rounded-lg border ${
                            action.completed ? 'bg-muted/50 opacity-60' : 'bg-card'
                          }`}
                        >
                          <Checkbox
                            checked={action.completed}
                            onCheckedChange={() => toggleActionComplete(action.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-2">
                            <Input
                              value={action.task}
                              onChange={(e) => updateActionItem(action.id, 'task', e.target.value)}
                              className={`font-medium border-0 p-0 h-auto ${action.completed ? 'line-through' : ''}`}
                            />
                            <Input
                              value={action.description || ''}
                              onChange={(e) => updateActionItem(action.id, 'description', e.target.value)}
                              placeholder="Add description..."
                              className="text-sm text-muted-foreground border-0 p-0 h-auto"
                            />
                            <div className="flex gap-4 text-xs">
                              <select
                                value={action.season}
                                onChange={(e) => updateActionItem(action.id, 'season', e.target.value)}
                                className="bg-muted rounded px-2 py-1"
                              >
                                <option value="any">Any Season</option>
                                <option value="spring">Spring</option>
                                <option value="summer">Summer</option>
                                <option value="fall">Fall</option>
                                <option value="winter">Winter</option>
                              </select>
                              <select
                                value={action.priority}
                                onChange={(e) => updateActionItem(action.id, 'priority', e.target.value)}
                                className={`rounded px-2 py-1 ${
                                  action.priority === 'high' ? 'bg-red-100 text-red-700' :
                                  action.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}
                              >
                                <option value="high">High Priority</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                              </select>
                              {action.estimated_time && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {action.estimated_time}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeActionItem(action.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
          
          {/* Shopping List Tab */}
          {activeTab === "shopping" && (
            <motion.div
              key="shopping"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Shopping List
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Estimated Total: ${shoppingList.reduce((sum, i) => sum + (i.estimated_cost || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={generateShoppingList}>
                      Auto-Generate
                    </Button>
                    <Button variant="outline" size="sm" onClick={addShoppingItem}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                    <Button variant="outline" size="sm" onClick={printShoppingList}>
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                    <Button size="sm" onClick={saveShoppingList} disabled={saving}>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {shoppingList.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No items in your shopping list</p>
                      <Button onClick={generateShoppingList} className="mt-4">
                        Generate Shopping List
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Category Headers */}
                      {['seeds', 'plants', 'soil', 'tools', 'materials', 'other'].map(category => {
                        const categoryItems = shoppingList.filter(i => i.category === category);
                        if (categoryItems.length === 0) return null;
                        
                        return (
                          <div key={category} className="mb-4">
                            <h4 className="font-medium capitalize text-sm mb-2 text-muted-foreground">
                              {category} ({categoryItems.length})
                            </h4>
                            {categoryItems.map(item => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border mb-2 ${
                                  item.purchased ? 'bg-muted/50 opacity-60' : 'bg-card'
                                }`}
                              >
                                <Checkbox
                                  checked={item.purchased}
                                  onCheckedChange={() => toggleShoppingPurchased(item.id)}
                                />
                                <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                                  <Input
                                    value={item.name}
                                    onChange={(e) => updateShoppingItem(item.id, 'name', e.target.value)}
                                    className={`border-0 p-0 h-auto ${item.purchased ? 'line-through' : ''}`}
                                  />
                                  <Input
                                    value={item.quantity}
                                    onChange={(e) => updateShoppingItem(item.id, 'quantity', e.target.value)}
                                    className="border-0 p-0 h-auto text-sm text-muted-foreground"
                                    placeholder="Qty"
                                  />
                                  <Input
                                    type="number"
                                    value={item.estimated_cost || ''}
                                    onChange={(e) => updateShoppingItem(item.id, 'estimated_cost', parseFloat(e.target.value) || null)}
                                    className="border-0 p-0 h-auto text-sm"
                                    placeholder="$0.00"
                                  />
                                  <Input
                                    value={item.notes || ''}
                                    onChange={(e) => updateShoppingItem(item.id, 'notes', e.target.value)}
                                    className="border-0 p-0 h-auto text-xs text-muted-foreground"
                                    placeholder="Notes"
                                  />
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeShoppingItem(item.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default GardenPlannerPage;
